import { FC, useState } from 'react'

import { useSuspenseQueries } from '@tanstack/react-query'

import { useToaster } from '@forto-dev/phoenix-ui-core'

import { RequiredActionV2 } from '@/features/workbench'
import { useLocalisation } from '@/utils/localisation'

import { houseShipmentByCodeQueryOptions } from '../../api/houseShipments.service'
import { useHouseShipmentHBLTypeUpdate, useShipmentInformationUpdate } from '../../api/useShipmentInformationUpdate'
import { HblType } from '../../domain/hbl'
import { HBLSelector } from '../details/hblSelector'

type ActionHBLTypeSelectorProps = {
  action: RequiredActionV2
  setSubmitCallback: (cb: (() => Promise<void>) | undefined) => void
}

export const ActionHBLTypeSelector: FC<ActionHBLTypeSelectorProps> = ({ action, setSubmitCallback }) => {
  const { translate } = useLocalisation()
  const [{ data: shipment }] = useSuspenseQueries({
    queries: [houseShipmentByCodeQueryOptions(action.entityId)],
  })

  const [selectedHblType, setSelectedHblType] = useState<HblType | undefined>(undefined)
  const updateMutation = useShipmentInformationUpdate()
  const updateMutationV2 = useHouseShipmentHBLTypeUpdate()

  const toaster = useToaster()

  const onHBLTypeChange = async (selectedHblType: HblType) => {
    if (!selectedHblType) {
      return
    }

    try {
      if (shipment.v2Flow) {
        await updateMutationV2.mutateAsync({
          houseShipmentCode: action.entityId,
          data: {
            type: selectedHblType,
          },
        })
      } else {
        await updateMutation.mutateAsync({
          shipmentId: action.entityId,
          data: {
            hblType: selectedHblType,
          },
        })
      }

      toaster({
        title: translate('required-actions.dialog.hblUpdateSuccess'),
        variant: 'positive',
      })
    } catch (error) {
      toaster({
        title: translate('required-actions.dialog.hblUpdateError'),
        description: error instanceof Error ? error.message : translate('errorDescription'),
        variant: 'negative',
      })
    }
  }

  return (
    <>
      <HBLSelector
        value={selectedHblType}
        onChange={(type) => {
          setSelectedHblType(type)
          setSubmitCallback(async () => await onHBLTypeChange(type))
        }}
      />
    </>
  )
}
